import { SliceComponentProps } from "@prismicio/react";
import _get from "lodash/get";
import { ParsedQs } from "qs";
import { RegisterOptions, useFormContext } from "react-hook-form";

import ErrorBoundary from "components/errorBoundary/ErrorBoundary";
import UploadPhotos from "components/form/uploadPhotos/UploadPhotos";
import { UploadPhotosFieldSlice } from "prismicio-types";
import { useGetProduct, useSetCustomisation } from "queries/product-queries";
import { useGetCustomisation } from "queries/product-queries";
import {
  FieldSliceContext,
  UploadedPhoto,
  UploadPhotosSchemaField,
} from "types/schema";
import {
  prismicFieldConfigurationError,
  SSR_CONFIGURATION_ERROR,
} from "utils/error-utils";
import { CANVAS_FALLBACK_CONTENT } from "utils/fallback-content";
import { useInterpolate } from "utils/prismic-utils";
import {
  isFieldType,
  useFindByKey,
  useGetUpsellForField,
} from "utils/schema-utils";

import styles from "../Fields.module.scss";

export default function UploadPhotosField({
  slice,
}: SliceComponentProps<UploadPhotosFieldSlice, FieldSliceContext>) {
  const {
    formState: { errors },
  } = useFormContext();

  const { data: product } = useGetProduct();

  const findByKey = useFindByKey();
  const { data: customisation } = useGetCustomisation();
  const interpolate = useInterpolate();

  const { mutate: setCustomisation } = useSetCustomisation();

  if (!product) {
    throw SSR_CONFIGURATION_ERROR;
  }

  const schemaKey = slice.primary.muse_schema_key;

  if (!schemaKey) {
    throw new Error("Missing Muse Schema Key");
  }

  const field = findByKey(schemaKey);
  const upsells = useGetUpsellForField(field?.urlKey);

  if (!field) {
    return null;
  }

  if (!isFieldType<UploadPhotosSchemaField>(field, "upload_photos")) {
    throw prismicFieldConfigurationError(
      schemaKey,
      "upload_photos",
      field.metas[0].canvasComponent
    );
  }

  /**
   * Save this photo to local storage immediately on upload
   *
   * This is to avoid confusion when photos are uploaded but the form isn't submitted
   */
  async function onPhotoUploaded(photo: UploadedPhoto) {
    setCustomisation({
      data: {
        ...customisation,
        [schemaKey!]: photo as unknown as ParsedQs,
      },
    });
  }

  /**
   * Remove photo immediately on delete.
   *
   * The empty string must be set in order to correctly merge the two objects.
   * It will be removed before entering localStorage by `setCustomisation`
   */
  async function onPhotoDeleted() {
    setCustomisation({ data: { ...customisation, [schemaKey!]: "" } });
  }

  const registerOptions: RegisterOptions = {
    required:
      field.flags?.presence === "required" &&
      interpolate(
        product.content.canvas.form_field_required_error_message ||
          CANVAS_FALLBACK_CONTENT.form_field_required_error_message
      ),
  };

  const error = errors && _get(errors, field.urlKey)?.message;

  return (
    <div className={styles.field}>
      {slice.primary.label && (
        <label className={styles.label} htmlFor={schemaKey}>
          {interpolate(slice.primary.label)}
        </label>
      )}

      <ErrorBoundary>
        <UploadPhotos
          aspectRatio={field.metas[0].aspectRatio}
          name={field.urlKey}
          onPhotoDeleted={onPhotoDeleted}
          onPhotoUploaded={onPhotoUploaded}
          registerOptions={registerOptions}
          upsells={upsells}
        />
      </ErrorBoundary>

      {error && <p className={styles.error}>{error as string}</p>}
    </div>
  );
}
